import {types as hlsTypes} from "hls-parser";
import {
    fetchText, getPreferredExt, hlsParse,
} from "@/components/DownloaderApp/utils";
import {
    getSubtitleTracks,
    sortVariantsByQuality,
    updateVariantUriToFullUrls
} from "@/components/DownloaderApp/playlists";
import type {DownloaderAppProps} from "./types";
import {PlaylistFetchError} from "@/components/DownloaderApp/download-errors";

export async function getDownloadConfig({
                                     video,
                                     platform,
                                     type,
                                     videoPlaylist,
                                     twitchVodDetails,
                                     chooseQuality,
                                     askForDownloadPreferences
                                 }, enforceExtenstion?: DownloaderAppProps["enforceExtenstion"],
                                     onSubtitlesDetected?: () => void) {
    const reportSubtitles = (playlist: hlsTypes.MasterPlaylist | hlsTypes.MediaPlaylist) => {
        if ("variants" in playlist && onSubtitlesDetected && getSubtitleTracks(playlist, videoPlaylist || video.url).length) {
            onSubtitlesDetected();
        }
    };

    let downloadConfig: {
        ext: ".mp4" | ".ts" | ".mp3" | ".vtt",
        trim: [number, number] | null,
        variantPlaylistText: string,
        variantPlaylistUrl: string,
        ongoingDownload: boolean,
        remux: null | "v2" | "v3",
        duration: number | null,
    } = {
        ext: enforceExtenstion === "vtt" ? ".vtt" : getPreferredExt(),
        trim: null,
        variantPlaylistText: "",
        variantPlaylistUrl: "",
        ongoingDownload: type === 'live',
        remux: null,
        duration: null
    }

    if (enforceExtenstion === "vtt" && type !== "vod" && type !== "live" && platform !== "direct-playlist") {
        throw new Error("Subtitle downloads require a VOD or master playlist URL with available subtitles.");
    }

    if (enforceExtenstion === "vtt" && !chooseQuality) {
        const fetchPlaylistText = async (url: string): Promise<string> => {
            if (!video.fetchWithExtension) return fetchText(url);
            if (!("sendMessage" in window) || typeof window.sendMessage !== "function") {
                throw new Error("The browser extension is not connected.");
            };
            return window.sendMessage("fetch-text", url);
        }
        const masterPlaylist = hlsParse(
            platform === "twitch" && twitchVodDetails?.masterPlaylistText
                ? twitchVodDetails.masterPlaylistText
                : await fetchPlaylistText(videoPlaylist)
        );
        if (!("variants" in masterPlaylist)) {
            throw new Error("This playlist has no subtitle tracks. Use a VOD or master playlist URL with available subtitles.");
        }
        reportSubtitles(masterPlaylist);
        const subtitles = getSubtitleTracks(masterPlaylist, videoPlaylist);
        const subtitle = subtitles.find(track => track.isDefault) ?? subtitles[0];
        if (!subtitle) throw new Error("This video has no downloadable subtitle tracks.");

        downloadConfig.variantPlaylistUrl = subtitle._url;
        downloadConfig.variantPlaylistText = await fetchPlaylistText(subtitle._url);
        const playlist = hlsParse(downloadConfig.variantPlaylistText);
        if (!("segments" in playlist) || !playlist.segments.length) {
            throw new Error("The selected subtitle playlist has no segments.");
        }
        if (!downloadConfig.ongoingDownload) {
            downloadConfig.duration = playlist.segments.reduce((total, segment) => total + segment.duration, 0);
        }
        return downloadConfig;
    }

    if (platform === "twitch" && type === "clip") {
        // it has been already handled...
    }
    if (platform === "kick" && type === "clip") {
        // no need to ask user about quality, as it's clip...
        downloadConfig.variantPlaylistUrl = videoPlaylist;
        downloadConfig.variantPlaylistText = await fetchText(downloadConfig.variantPlaylistUrl)
    }
    if (platform === "twitch" && (type === "vod" || type === "live")) {
        if (twitchVodDetails.isSubscriberOnly && twitchVodDetails.url) {
            downloadConfig.variantPlaylistUrl = twitchVodDetails.url;
            downloadConfig.variantPlaylistText = await window.sendMessage("fetch-text", downloadConfig.variantPlaylistUrl);

            // isSubscriberOnly

            let variants = [];
            if (chooseQuality) {
                try {
                    const result = await fetch('/api/twitch/variants?source=' + encodeURIComponent(downloadConfig.variantPlaylistUrl)).then(r => r.json());
                    variants = result.variants.map(([title, uri]) => ({
                        title,
                        uri
                    }));
                } catch (err) {

                }
            }

            const masterPlaylist = {
                _url: downloadConfig.variantPlaylistUrl, // its needod to preserve session storage trim
                variants: [{
                    title: "Highest",
                    uri: downloadConfig.variantPlaylistUrl,
                }, ...variants]
            };

            const hqPlaylist = window.sendMessage("fetch-text", downloadConfig.variantPlaylistUrl).then(hlsParse);
            if (chooseQuality) {
                const downloadPreferences = await askForDownloadPreferences(masterPlaylist, hqPlaylist, video);
                if (!downloadPreferences) return;
                downloadConfig.variantPlaylistUrl = downloadPreferences.quality.uri
                downloadConfig.trim = downloadPreferences.trim
                downloadConfig.ext = downloadPreferences.ext
                downloadConfig.remux = downloadPreferences.remux
                if (type === 'live' && downloadPreferences.duration !== "live stream") {
                    downloadConfig.ongoingDownload = false;
                }
            }
        } else {
            const masterPlaylistUrl = twitchVodDetails.masterPlaylistText;
            const masterPlaylist = hlsParse(masterPlaylistUrl);
            reportSubtitles(masterPlaylist);

            const unique = twitchVodDetails.availableFormats.filter(([, uri]) => {
                return !masterPlaylist.variants.find((format) => uri === format.uri)
            })

            masterPlaylist.variants = [...unique.map(([title, uri]) => ({
                title,
                uri
            })), ...masterPlaylist.variants];

            updateVariantUriToFullUrls(masterPlaylist, masterPlaylistUrl)

            if (chooseQuality) {
                const hqPlaylist = window.sendMessage("fetch-text", masterPlaylist.variants[0]._url).then(hlsParse)

                const downloadPreferences = await askForDownloadPreferences(masterPlaylist, hqPlaylist, video)
                if (!downloadPreferences) return;

                downloadConfig.variantPlaylistUrl = downloadPreferences.quality._url
                downloadConfig.trim = downloadPreferences.trim
                downloadConfig.ext = downloadPreferences.ext
                downloadConfig.remux = downloadPreferences.remux
            }
            if (!downloadConfig.variantPlaylistUrl) {
                // variantPlaylistUrl = twitchVodDetails.url
                downloadConfig.variantPlaylistUrl = masterPlaylist.variants[0]._url
            }

            downloadConfig.variantPlaylistText = await window.sendMessage("fetch-text", downloadConfig.variantPlaylistUrl)
        }
    }
    if (platform === "kick" && type === "vod") {
        let masterPlaylist;
        let videoPlaylistText = '';
        try {
            videoPlaylistText = await fetchText(videoPlaylist);
            masterPlaylist = hlsParse(videoPlaylistText)
        }catch(err){
            window.posthog?.logger.info('failed to fetch kick vod playlist', {
                videoPlaylist,
                video,
                videoPlaylistText,
                err: err?.message ?? ''
            })
            throw err;
        }

        console.log({masterPlaylist})

        reportSubtitles(masterPlaylist);
        updateVariantUriToFullUrls(masterPlaylist, videoPlaylist)
        const hqPlaylistUrl = masterPlaylist.variants[0]._url;

        if (chooseQuality) {
            const hqPlaylist = fetchText(hqPlaylistUrl).then(hlsParse)
            console.log(await hqPlaylist)
            const downloadPreferences = await askForDownloadPreferences(masterPlaylist, hqPlaylist, video)
            if (!downloadPreferences) return;

            downloadConfig.trim = downloadPreferences.trim
            downloadConfig.ext = downloadPreferences.ext
            downloadConfig.remux = downloadPreferences.remux

            downloadConfig.variantPlaylistUrl = downloadPreferences.quality._url
        }
        if (!downloadConfig.variantPlaylistUrl) {
            downloadConfig.variantPlaylistUrl = hqPlaylistUrl;
        }
        downloadConfig.variantPlaylistText = await fetchText(downloadConfig.variantPlaylistUrl)
    }
    if (platform === "kick" && type === "live") {
        const masterPlaylist = hlsParse(await fetchText(video.videoPlaylist))

        reportSubtitles(masterPlaylist);
        updateVariantUriToFullUrls(masterPlaylist, videoPlaylist)
        const hqPlaylistUrl = masterPlaylist.variants[0]._url;

        if (chooseQuality) {
            const hqPlaylist = fetchText(hqPlaylistUrl).then(hlsParse)
            const downloadPreferences = await askForDownloadPreferences(masterPlaylist, hqPlaylist, video)
            if (!downloadPreferences) return;

            downloadConfig.trim = downloadPreferences.trim
            downloadConfig.ext = downloadPreferences.ext
            downloadConfig.remux = downloadPreferences.remux

            if (downloadPreferences.duration !== "live stream") {
                video.type = "vod";
                downloadConfig.ongoingDownload = false;
            }

            downloadConfig.variantPlaylistUrl = downloadPreferences.quality._url
        }
        if (!downloadConfig.variantPlaylistUrl) {
            downloadConfig.variantPlaylistUrl = hqPlaylistUrl;
        }
        downloadConfig.variantPlaylistText = await fetchText(downloadConfig.variantPlaylistUrl)
    }

    if (platform === "rumble") {
        let masterPlaylistText: string;
        try {
            masterPlaylistText = await fetchText(videoPlaylist);
        } catch (err) {
            throw new PlaylistFetchError(undefined, err);
        }
        const parsedPlaylist = hlsParse(masterPlaylistText);

        if (type === 'live') {
            parsedPlaylist.variants = parsedPlaylist.variants.filter(a => !!a.uri.match(/_(\d+)p/)).map(item => ({
                ...item,
                // uri: item.uri.replace('i0.', 'i1_DVR.'),
                uri: item.uri.endsWith("_DVR.m3u8") ? item.uri : item.uri.replace('.m3u8', '_DVR.m3u8'),
            }))
        }

        if (!parsedPlaylist.isMasterPlaylist) {
            if (enforceExtenstion === "vtt") {
                throw new Error("This playlist has no subtitle tracks. Use a VOD or master playlist URL with available subtitles.");
            }
            downloadConfig.variantPlaylistUrl = videoPlaylist;
            downloadConfig.variantPlaylistText = masterPlaylistText;
        } else {
            const masterPlaylist = parsedPlaylist;
            reportSubtitles(masterPlaylist);
            updateVariantUriToFullUrls(masterPlaylist, videoPlaylist);
            const hqPlaylistUrl = masterPlaylist.variants[0]?._url;
            if (!hqPlaylistUrl) {
                throw new PlaylistFetchError("The video playlist has no downloadable streams");
            }

            if (chooseQuality) {
                const hqPlaylist = fetchText(hqPlaylistUrl).then(hlsParse);
                const downloadPreferences = await askForDownloadPreferences(masterPlaylist, hqPlaylist, video);
                if (!downloadPreferences) return;

                downloadConfig.variantPlaylistUrl = downloadPreferences.quality._url;
                downloadConfig.trim = downloadPreferences.trim;
                downloadConfig.ext = downloadPreferences.ext;
                downloadConfig.remux = downloadPreferences.remux
            }
            if (!downloadConfig.variantPlaylistUrl) {
                downloadConfig.variantPlaylistUrl = hqPlaylistUrl;
            }
            downloadConfig.variantPlaylistText = await fetchText(downloadConfig.variantPlaylistUrl);
        }
    }

    if (platform === "direct-playlist") {
        // Helper to fetch playlist text (use extension if needed, determined earlier)
        const fetchPlaylistText = video.fetchWithExtension
            ? (url: string) => window.sendMessage("fetch-text", url)
            : fetchText;

        const playlistText = await fetchPlaylistText(videoPlaylist);
        const parsedPlaylist = hlsParse(playlistText);

        if (!parsedPlaylist.isMasterPlaylist) {
            if (enforceExtenstion === "vtt") {
                throw new Error("This playlist has no subtitle tracks. Use a VOD or master playlist URL with available subtitles.");
            }
            if (chooseQuality) {
                const masterPlaylist = {
                    _url: videoPlaylist,
                    variants: [{
                        title: "Source",
                        uri: videoPlaylist,
                        _url: videoPlaylist,
                    }],
                };
                const downloadPreferences = await askForDownloadPreferences(masterPlaylist, Promise.resolve(parsedPlaylist), video);
                if (!downloadPreferences) return;

                downloadConfig.trim = downloadPreferences.trim;
                downloadConfig.ext = downloadPreferences.ext;
                downloadConfig.remux = downloadPreferences.remux;
            }
            downloadConfig.variantPlaylistUrl = videoPlaylist;
            downloadConfig.variantPlaylistText = playlistText;
        } else {
            // master playlist
            const masterPlaylist = parsedPlaylist as hlsTypes.MasterPlaylist;
            reportSubtitles(masterPlaylist);
            sortVariantsByQuality(masterPlaylist)
            updateVariantUriToFullUrls(masterPlaylist, videoPlaylist);

            const hqPlaylistUrl = masterPlaylist.variants[0]._url
            if (chooseQuality) {
                const hqPlaylist = fetchPlaylistText(hqPlaylistUrl).then(hlsParse)
                const downloadPreferences = await askForDownloadPreferences(masterPlaylist, hqPlaylist, video)
                if (!downloadPreferences) return;

                downloadConfig.variantPlaylistUrl = downloadPreferences.quality._url
                downloadConfig.trim = downloadPreferences.trim
                downloadConfig.ext = downloadPreferences.ext
                downloadConfig.remux = downloadPreferences.remux
            }
            if (!downloadConfig.variantPlaylistUrl) {
                downloadConfig.variantPlaylistUrl = hqPlaylistUrl;
            }
            downloadConfig.variantPlaylistText = await fetchPlaylistText(downloadConfig.variantPlaylistUrl);
        }
    }
    if (!downloadConfig.ongoingDownload && downloadConfig.variantPlaylistText) {
        const playlist = hlsParse(downloadConfig.variantPlaylistText);
        const segments = downloadConfig.trim
            ? playlist.segments.slice(downloadConfig.trim[0], downloadConfig.trim[1])
            : playlist.segments;

        downloadConfig.duration = segments.reduce((total, segment) => total + segment.duration, 0);
    }

    return downloadConfig
}
